import React from 'react';
import PropTypes from 'prop-types';

import { Title, Controls, Control } from 'features/App';
import AutoSyncStatus from 'components/AutoSyncStatus';
import FeatureNotSupported from 'components/FeatureNotSupported';
import Message from 'components/Message';
import Isvg from 'react-inlinesvg';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { classListPropTypes, intlMessage } from 'constants/propTypes';
import { useIsServicePricingProofEnabled } from 'hooks/useIsServicePricingProofEnabled';

import classesListMessages from './ClassesList.messages';
import ClassesListRow from './ClassesListRow/ClassesListRow';
import './ClassesList.scss';

const { element } = bem('classes-list');

const ClassesList = ({
  classes,
  classTitleLink,
  featureIsSupported,
  locale,
  messages,
  noLinkedClasses,
  showEnableClassToggle,
  showPricingColumns: showPricingColumnsProp,
  updateIsClassEnabled,
}) => {
  const isPricingProofEnabled = useIsServicePricingProofEnabled();
  const showPricingColumns = showPricingColumnsProp ?? isPricingProofEnabled === true;

  let content = (
    <>
      <div className={element('list')}>
        <div className={element('row-header', 'grid grid--middle')}>
          <div className="grid__item 1/2">
            <Message
              as="div"
              className="pt-regular pb-regular pl-large text--medium"
              message={classesListMessages.name}
            />
          </div>
          {showPricingColumns && (
            <div className="grid__item 1/6 text--right">
              <Message
                as="div"
                className="pt-regular pb-regular text--medium"
                message={classesListMessages.payoutLabel}
                values={{
                  icon: () => (
                    <TooltipToggle name="classes-list-payout-tooltip">
                      <Isvg src={ICONS.infoGrey} />
                    </TooltipToggle>
                  ),
                }}
              />
              <TooltipContent hoverable name="classes-list-payout-tooltip" place="top">
                <Message
                  message={classesListMessages.payoutTooltip}
                  values={{
                    link: text => (
                      <a href={makePath.partnerTaxDefinition(locale)} target="_blank" rel="noopener noreferrer">
                        {text}
                      </a>
                    ),
                  }}
                />
              </TooltipContent>
            </div>
          )}
          <div className={showPricingColumns ? 'grid__item 1/3 text--right' : 'grid__item 1/2 text--right'}>
            <Message
              as="div"
              className="pt-regular pr-large pb-regular text--medium"
              message={classesListMessages.bookable}
            />
          </div>
        </div>
        {classes.map(venueClass => (
          <ClassesListRow
            key={venueClass.classId}
            updateIsClassEnabled={updateIsClassEnabled}
            showEnableClassToggle={showEnableClassToggle}
            showPricingColumns={showPricingColumns}
            classTitleLink={classTitleLink}
            venueClass={venueClass}
          />
        ))}
      </div>
    </>
  );

  if (!featureIsSupported) {
    content = <FeatureNotSupported />;
  } else if (noLinkedClasses) {
    content = (
      <div className="splash">
        <section className="splash__content">
          <Message as="p" message={messages.emptyList} />
        </section>
      </div>
    );
  }

  return (
    <>
      <Message message={messages.title} as={Title} />
      <Controls>
        <Control type="trough" className="mb-medium">
          <Message
            message={classesListMessages.contractDescription}
            as="div"
            className={element('description')}
            values={{
              a: text => (
                <a href={makePath.approvedWellnessServices(locale)} target="_blank" rel="noreferrer">
                  {text}
                </a>
              ),
            }}
          />
        </Control>
        {featureIsSupported && !noLinkedClasses && (
          <Control type="trough">
            <AutoSyncStatus />
          </Control>
        )}
      </Controls>
      {content}
    </>
  );
};

ClassesList.propTypes = {
  classes: classListPropTypes,
  classTitleLink: PropTypes.func,
  featureIsSupported: PropTypes.bool,
  locale: PropTypes.string,
  messages: PropTypes.shape({
    emptyList: intlMessage,
    title: intlMessage,
    confirmClassesHeader: intlMessage,
    confirmClassesDescription: intlMessage,
    editClassesHeader: intlMessage,
    editClassesDescription: intlMessage,
    next: intlMessage,
  }),
  noLinkedClasses: PropTypes.bool,
  showEnableClassToggle: PropTypes.bool,
  showPricingColumns: PropTypes.bool,
  updateIsClassEnabled: PropTypes.func,
};

export default ClassesList;
